import { type ApiErrorResponse, useIntl } from '@capsicum/common';
import { type AxiosError } from 'axios';
import { useCallback, useEffect } from 'react';

import { ProductCheckoutErrors, ProductCheckoutErrorsEnum } from '../models';

export type UseCheckoutErrorHandlerProps = {
	error?: AxiosError<ApiErrorResponse, unknown> | null;
	onFailedCheckout?: (errorMessage: string, errorTitle?: string) => void;
};

export const useCheckoutErrorHandler = ({ error, onFailedCheckout }: UseCheckoutErrorHandlerProps = {}) => {
	const { $t } = useIntl();

	const getCheckoutErrorTranslation = useCallback(
		(errorType: ProductCheckoutErrors) => {
			switch (errorType) {
				case ProductCheckoutErrors.PRODUCT_NOT_ENABLED:
					return $t('rewards.fanshop.orderSummary.productOrProductVariantNotEnabledError');
				case ProductCheckoutErrors.USER_MET_MAX_PURCHASE:
					return $t('rewards.fanshop.orderSummary.productOrderLimitExceededError');
				case ProductCheckoutErrors.PURCHASE_WILL_EXCEED_MAX_ALLOWED:
					return $t('rewards.fanshop.orderSummary.productOrderAmountLimitExceededError');
				case ProductCheckoutErrors.PRODUCT_OUT_OF_STOCK:
					return $t('rewards.fanshop.orderSummary.productOutOfStockError');
				case ProductCheckoutErrors.PRODUCT_NOT_ENOUGH_STOCK:
					return $t('rewards.fanshop.orderSummary.notEnoughProductStockError');
				case ProductCheckoutErrors.NOT_ENOUGH_FUNDS_IN_BALANCE:
					return $t('rewards.fanshop.orderSummary.notEnoughFundsInBalanceErrorRewardPoints');
				case ProductCheckoutErrors.PRODUCT_BANNED_IN_USER_COUNTRY:
					return $t('rewards.fanshop.orderSummary.productBannedInUserCountryError');
				case ProductCheckoutErrors.PRODUCT_TIER_LEVEL_ACCESS_NOT_MET:
					return $t('rewards.fanshop.orderSummary.productTierLevelAcessNotMetError');
				case ProductCheckoutErrors.PRODUCT_VARIANT_IS_NOT_YET_REDEEMABLE:
					return $t('rewards.fanshop.orderSummary.productVariantIsNotYetRedeemableError');
				case ProductCheckoutErrors.REQUESTED_QUANTITY_NOT_ALLOWED:
					return $t('rewards.fanshop.orderSummary.quantityNotAllowed');
			}
		},
		[$t]
	);

	const handleCheckoutErrorMessages = useCallback(
		(checkoutErrors: ApiErrorResponse['errors']) => {
			if (!checkoutErrors) return;

			checkoutErrors.forEach(error => {
				const errorType = ProductCheckoutErrorsEnum[error.code as string];

				if (!errorType) return;

				const message = getCheckoutErrorTranslation(errorType);

				// TODO: add title later on
				onFailedCheckout?.(message);
			});
		},
		[getCheckoutErrorTranslation, onFailedCheckout]
	);

	useEffect(() => {
		if (error) handleCheckoutErrorMessages(error?.response?.data?.errors);
	}, [error, handleCheckoutErrorMessages]);
};
